import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, RotateCcw, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { loadLatestOrder, getCurrentStepIndex, STEP_ORDER, type TrackedOrder } from '../utils/orderTracking';
// The personalized greeting replaces the marketing hero headline +
// tagline once the visitor has built up a relationship with us:
//
//   - first-time           → no saved order anywhere on the device
//                            → show the regular marketing hero
//   - just-delivered-once  → 1st return visit after a delivered order
//                            → still show the marketing hero, but the
//                              existing small WelcomeBackCard pill greets
//                              them and offers one-tap reorder
//   - repeat               → 2nd+ visit after a delivered order
//                            → drop the marketing pitch and the small
//                              pill; replace with a personal "Good
//                              morning, {firstName}" hero
//   - in-flight            → there's an active order from this device
//                            that hasn't reached the "delivered" step
//                            yet → personal greeting with a track CTA
//                            instead of a reorder CTA (regardless of
//                            visit count — this is the more urgent,
//                            more useful surface in that moment)
//
// All state is derived from localStorage so the system works without
// any auth. On a new device / private window the visitor naturally
// falls back to 'first-time', which is fine.
type HeroVariant = 'first-time' | 'just-delivered-once' | 'repeat' | 'in-flight';
const LAST_ORDER_KEY = '253gravel_last_order';
const VISITS_KEY = '253gravel_visits';
type SavedOrder = {
  items: {
    id?: string;
    category: string;
    product: string;
    quantity: number;
    pricePerYard: number;
  }[];
  addressInput?: string;
  name?: string;
  phone?: string;
  email?: string;
};
function getFirstName(fullName?: string): string | null {
  if (!fullName) return null;
  // First whitespace-separated token, stripped of trailing punctuation
  // and capped at ~16 chars so an obscure all-lowercase or all-caps
  // submission doesn't make the headline look broken. Reject single
  // letters and emoji-only inputs — they read as obviously wrong.
  const token = fullName.trim().split(/\s+/)[0] || '';
  const cleaned = token.replace(/[^\p{L}\p{M}'\-]/gu, '');
  if (cleaned.length < 2) return null;
  // Normalise casing: capital first letter, lowercase rest. Leaves
  // names with internal capitalisation (e.g. "McKay") alone if they
  // came in that way.
  const isAllSameCase = cleaned === cleaned.toLowerCase() || cleaned === cleaned.toUpperCase();
  const display = isAllSameCase ? cleaned[0].toUpperCase() + cleaned.slice(1).toLowerCase() : cleaned;
  return display.slice(0, 16);
}
function timeOfDayGreeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Hi';
}
// Rotating subhead questions for the repeat-visitor variant. Each phrase
// asks the same thing ("how can we help?") but in a different voice, so
// returning customers don't see the exact same line every visit. One is
// picked at mount time, so it's stable while the user is on the page
// but refreshes on the next visit.
const REPEAT_GREETING_QUESTIONS = ['What can we get you today?', 'What are we hauling for you today?', 'Where are we delivering this round?', 'Ready for another load?', "What's the next project?"];
function pickRepeatGreetingQuestion(): string {
  const i = Math.floor(Math.random() * REPEAT_GREETING_QUESTIONS.length);
  return REPEAT_GREETING_QUESTIONS[i];
}
export function usePersonalGreeting(): {
  variant: HeroVariant;
  firstName: string | null;
  savedOrder: SavedOrder | null;
  activeOrder: TrackedOrder | null;
  activeStepIdx: number;
} {
  const [variant, setVariant] = useState<HeroVariant>('first-time');
  const [firstName, setFirstName] = useState<string | null>(null);
  const [savedOrder, setSavedOrder] = useState<SavedOrder | null>(null);
  const [activeOrder, setActiveOrder] = useState<TrackedOrder | null>(null);
  const [activeStepIdx, setActiveStepIdx] = useState<number>(0);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_ORDER_KEY);
      if (!raw) {
        setVariant('first-time');
        return;
      }
      const parsed: SavedOrder = JSON.parse(raw);
      if (!parsed?.items?.length) {
        setVariant('first-time');
        return;
      }
      setSavedOrder(parsed);
      setFirstName(getFirstName(parsed.name));
      // In-flight order? Same source the WelcomeBackCard uses so the
      // two surfaces stay in lockstep.
      const latest = loadLatestOrder();
      if (latest) {
        const stepIdx = getCurrentStepIndex(latest);
        if (stepIdx < STEP_ORDER.length - 1) {
          setActiveOrder(latest);
          setActiveStepIdx(stepIdx);
          setVariant('in-flight');
          return;
        }
      }
      // No active order — pick between just-delivered-once and repeat
      // based on the same visits counter the WelcomeBackCard maintains.
      // `visits >= 2` means we have already given the customer at least
      // one shot at the small reorder pill on a prior visit, so it's
      // time to retire it and let the big greeting take the hero slot.
      const visits = parseInt(localStorage.getItem(VISITS_KEY) || '1', 10);
      setVariant(visits >= 2 ? 'repeat' : 'just-delivered-once');
    } catch {
      // Any localStorage / JSON issue → treat as first-time and let the
      // default marketing hero render. Failing closed never hurts here.
      setVariant('first-time');
    }
  }, []);
  return {
    variant,
    firstName,
    savedOrder,
    activeOrder,
    activeStepIdx
  };
}
// Friendly label for the active-order step. Matches the same vocab the
// /track page and WelcomeBackCard use so the customer sees consistent
// language across every touchpoint.
function stepLabelFor(stepIdx: number): string {
  switch (STEP_ORDER[stepIdx]) {
    case 'confirmed':
      return 'Order confirmed';
    case 'scheduled':
      return 'Scheduled for delivery';
    case 'dispatched':
      return 'Dispatched from yard';
    case 'en_route':
      return 'Driver on the way';
    default:
      return 'In progress';
  }
}
export function PersonalGreeting({
  variant,
  firstName,
  savedOrder,
  activeOrder,
  activeStepIdx
}: {
  variant: HeroVariant;
  firstName: string | null;
  savedOrder: SavedOrder | null;
  activeOrder: TrackedOrder | null;
  activeStepIdx: number;
}) {
  const {
    loadSavedCart,
    setDeliveryInfo,
    openCart
  } = useCart();
  // Locked at first render so the phrase is stable while the user is on
  // the page — only a fresh page load rolls a new one.
  const [repeatQuestion] = useState<string>(() => pickRepeatGreetingQuestion());
  if (variant !== 'in-flight' && variant !== 'repeat') return null;
  // Display name fallback — if we couldn't pull a clean first name from
  // the saved order (rare but possible: blank, single letter, emoji),
  // skip the name and lead with the generic time-of-day greeting alone.
  const greetingHead = firstName ? `${timeOfDayGreeting()}, ${firstName}` : timeOfDayGreeting();
  const handleReorder = () => {
    if (!savedOrder) return;
    loadSavedCart(savedOrder.items.map(i => ({
      id: `${i.category}-${i.product}-${Date.now()}-${Math.random()}`,
      category: i.category,
      product: i.product,
      quantity: i.quantity,
      pricePerYard: i.pricePerYard
    })));
    if (savedOrder.addressInput) {
      setDeliveryInfo(savedOrder.addressInput, 0, null);
    }
    openCart();
  };
  // -------- IN-FLIGHT VARIANT --------
  // The customer just placed an order and the truck hasn't arrived yet.
  // The most useful surface in that moment is order status + a one-tap
  // jump to the live tracking page. Reorder is a secondary affordance.
  if (variant === 'in-flight' && activeOrder) {
    const stepLabel = stepLabelFor(activeStepIdx);
    const shortAddress = (activeOrder.address || '').split(',')[0].trim();
    const totalYards = activeOrder.items.reduce((s, i) => s + i.quantity, 0);
    const itemSummary = activeOrder.items.length > 1 ? `${totalYards} yds across ${activeOrder.items.length} materials` : `${activeOrder.items[0].quantity} yds ${activeOrder.items[0].product}`;
    return <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1]
    }} className="mb-4 md:mb-6" data-id="element-1390">
        <div className="inline-flex items-center gap-2 mb-4 md:mb-5 px-3 py-1.5 rounded-full bg-[#059F40]/15 border border-[#059F40]/35 backdrop-blur-md" data-id="element-1391">
          <span className="relative flex h-2 w-2" data-id="element-1392">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#059F40] opacity-75" data-id="element-1393" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#059F40]" data-id="element-1394" />
          </span>
          <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#22c95c]" data-id="element-1395">
            {stepLabel}
          </span>
        </div>

        <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1] mb-3 md:mb-4" data-id="element-1396">
          {greetingHead}
          <span className="block mt-1 md:mt-2 text-[#059F40]" data-id="element-1397">
            Your load is on the way.
          </span>
        </h1>

        {/* Delivery details stack on their own lines so a long address
                     doesn't break mid-word on mobile. White arrow on the address
                     line reinforces the "material → destination" flow. */}
        <div className="mb-5 md:mb-6 max-w-2xl space-y-1.5" data-id="element-1398">
          <p className="text-lg md:text-xl font-semibold font-sub text-white/95" data-id="element-1399">
            {itemSummary}
          </p>
          {shortAddress && <p className="text-lg md:text-xl font-semibold font-sub text-white/95 flex items-baseline gap-2 justify-center md:justify-start" data-id="element-1400">
              <span className="text-white shrink-0" aria-hidden="true" data-id="element-1401">
                →
              </span>
              <span data-id="element-1402">{shortAddress}</span>
            </p>}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:items-center items-center sm:items-stretch mb-2 md:justify-start" data-id="element-1403">
          <Link to={`/track/${encodeURIComponent(activeOrder.orderId)}`} className="group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-[#059F40] hover:bg-[#048F3A] text-white font-bold text-base transition-colors shadow-[0_0_18px_rgba(5,159,64,0.35)]" aria-label="Track your delivery" data-id="element-1404">
            <MapPin className="w-4 h-4" data-id="element-1405" />
            Track delivery
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-1406" />
          </Link>
          <span className="text-sm text-white/70 text-center md:text-left sm:ml-1" data-id="element-1407">
            Need another load? Build a new order below.
          </span>
        </div>
      </motion.div>;
  }
  // -------- REPEAT VARIANT --------
  // No active order, but they've come back at least once since their
  // last delivery. They already know what we do; they're here to
  // buy again. Greet by name and put a reorder shortcut front and centre.
  if (!savedOrder) return null;
  const firstItem = savedOrder.items[0];
  const totalYards = savedOrder.items.reduce((s, i) => s + i.quantity, 0);
  const reorderSummary = savedOrder.items.length > 1 ? `${totalYards} yds across ${savedOrder.items.length} materials` : `${firstItem.quantity} yds ${firstItem.product}`;
  return <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: [0.16, 1, 0.3, 1]
  }} className="mb-4 md:mb-6" data-id="element-1408">
      <div className="inline-flex items-center gap-2 mb-4 md:mb-5 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-md" data-id="element-1409">
        <Truck className="w-3.5 h-3.5 text-[#22c95c]" data-id="element-1410" />
        <span className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/90" data-id="element-1411">
          Welcome back
        </span>
      </div>

      <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1] mb-3 md:mb-4" data-id="element-1412">
        {greetingHead}.
        <span className="block mt-1 md:mt-2 text-[#059F40]" data-id="element-1413">
          {repeatQuestion}
        </span>
      </h1>

      <p className="text-lg md:text-xl text-white/85 font-semibold font-sub mb-5 md:mb-6 max-w-2xl" data-id="element-1414">
        Pick up where you left off — same address, same materials, or build a
        new order below.
      </p>

      {/* Inline reorder shortcut — replaces the small pill version of
                    the same affordance. Keeps the previous order visible at a
                    glance and is a single tap from filling the cart. */}
      <div className="flex justify-center md:justify-start" data-id="element-1415">
        <button type="button" onClick={handleReorder} className="group inline-flex items-center gap-3 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 hover:border-[#059F40]/50 backdrop-blur-md transition-all max-w-full" data-id="element-1416">
          <span className="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-[#059F40]/20 border border-[#059F40]/40" data-id="element-1417">
            <RotateCcw className="w-4 h-4 text-[#22c95c]" data-id="element-1418" />
          </span>
          <span className="flex flex-col items-start min-w-0" data-id="element-1419">
            <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-white/60" data-id="element-1420">
              Reorder last delivery
            </span>
            <span className="text-sm sm:text-base font-bold text-white truncate max-w-[260px] sm:max-w-none" data-id="element-1421">
              {reorderSummary}
            </span>
          </span>
          <span className="inline-flex items-center gap-1 text-sm font-bold text-[#22c95c] shrink-0 ml-1" data-id="element-1422">
            Reorder
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-1423" />
          </span>
        </button>
      </div>
    </motion.div>;
}